import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { removeInlineColorStyles } from '../hooks/useMinorTestResultPage';
import { useResultTextBlockStyles } from './ResultTextBlock.styles';

type ResultTextBlockProps = {
  html: string;
  maxCollapsedHeight?: number;
};

const TEXT_INNER_MAX_HEIGHT = 296;

const ResultTextBlock = ({ html, maxCollapsedHeight = 336 }: ResultTextBlockProps) => {
  const classes = useResultTextBlockStyles();
  const textContentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const cleanText = removeInlineColorStyles(html);

  useEffect(() => {
    const checkTextHeight = () => {
      if (!textContentRef.current || !cleanText || isExpanded) return;

      const element = textContentRef.current;
      const rect = element.getBoundingClientRect();

      if (rect.height === 0) {
        setTimeout(checkTextHeight, 50);
        return;
      }

      const originalMaxHeight = element.style.maxHeight;
      const originalOverflow = element.style.overflow;

      element.style.maxHeight = 'none';
      element.style.overflow = 'visible';
      const naturalHeight = element.scrollHeight;
      element.style.maxHeight = originalMaxHeight;
      element.style.overflow = originalOverflow;

      setIsOverflowing(naturalHeight > TEXT_INNER_MAX_HEIGHT);
    };

    if (cleanText) {
      setTimeout(checkTextHeight, 100);
    }
  }, [cleanText, isExpanded, maxCollapsedHeight]);

  if (!cleanText) {
    return null;
  }

  const shouldShowExpand = isOverflowing && !isExpanded;
  const isCollapsed = isOverflowing && !isExpanded;

  return (
    <div className={classes.textBlockSection}>
      <div className={clsx(classes.textCard, isCollapsed && classes.textCardCollapsed)}>
        <div
          ref={textContentRef}
          className={classes.textScrollArea}
          style={{
            maxHeight: isCollapsed ? TEXT_INNER_MAX_HEIGHT : 'none',
            overflow: isCollapsed ? 'hidden' : 'visible',
          }}>
          <div className={classes.textContent}>{ReactHtmlParser(cleanText)}</div>
          {shouldShowExpand && <div className={classes.textFade} aria-hidden="true" />}
        </div>

        {isOverflowing && isExpanded && (
          <button type="button" className={classes.collapseButton} onClick={() => setIsExpanded(false)}>
            Voir moins
          </button>
        )}
      </div>

      {shouldShowExpand && (
        <button type="button" className={classes.expandButton} onClick={() => setIsExpanded(true)}>
          Afficher le reste de mes résultats
          <FormatListBulletedIcon style={{ width: 16, height: 16 }} />
        </button>
      )}
    </div>
  );
};

export default ResultTextBlock;
